import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Alert, Switch } from '@mantine/core';

import {
  CUBOH_MENU_CHILD_MAP,
  MENU_OBJECT_TYPES,
  TYPE_TO_READABLE_STRING_MAP,
} from 'constants/menuManagement';

import { useDisclosure } from '@mantine/hooks';

import AddItemDisplayDrawer from 'components/MenuManagement/PointOfSale/AddItemDisplayDrawer';
import SelectItemFromPosDrawer from 'components/MenuManagement/PointOfSale/SelectItemFromPosDrawer';

import { useMenuManagement } from 'hooks/useMenuManagement';
import { BellIconMd } from '@chownow/pantry/icons';



const { ResyncSelect } = SelectItemFromPosDrawer;

export default function AddItemFromPosDrawer({
  itemType,
  parentRecord,
  opened,
  closeAddItemFromPosDrawer,
  openAddItemFromPosDrawer,
  openAddTypeChoiceDrawer,
}) {
  const { posRefreshInProgress } = useMenuManagement();
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [duplicateChildren, setDuplicateChildren] = useState(true);
  const [message, setMessage] = useState('No Items Found');
  const [isLoading, setIsLoading] = useState(true);
  const [addItemDisplayDrawerOpen, addItemDisplayDrawerHandlers] = useDisclosure(false);

  // eslint-disable-next-line consistent-return
  const duplicateChildrenToggle = (() => {
    if (
      itemType !== MENU_OBJECT_TYPES.modifiers &&
      itemType !== MENU_OBJECT_TYPES.items &&
      itemType !== MENU_OBJECT_TYPES.modifierGroups
    ) {
      return (
        <Switch
          checked={duplicateChildren}
          label={`Add connected ${TYPE_TO_READABLE_STRING_MAP[CUBOH_MENU_CHILD_MAP[itemType]]}s`}
          onChange={() => setDuplicateChildren(!duplicateChildren)}
          mt="lg"
        />
      );
    }
  })();

  return (
    <>
      <SelectItemFromPosDrawer
        parentRecord={parentRecord}
        itemType={itemType}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        opened={opened}
        closeAddItemFromPosDrawer={closeAddItemFromPosDrawer}
        openAddItemDisplayDrawer={addItemDisplayDrawerHandlers.open}
        closeAddItemDisplayDrawer={addItemDisplayDrawerHandlers.close}
        openAddTypeChoiceDrawer={openAddTypeChoiceDrawer}
        dropdownOptions={dropdownOptions}
        setDropdownOptions={setDropdownOptions}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setMessage={setMessage}
      >
        <div>
          {posRefreshInProgress ? (
            <Alert variant="warning" icon={<BellIconMd />} mb="md">
              A POS refresh is currently in progress.&nbsp; Please wait for refresh to complete
              before editing your menu.
            </Alert>
          ) : (
            itemType !== MENU_OBJECT_TYPES.categories && (
              <Alert variant="blue" icon={<BellIconMd />} mb="md">
                To avoid POS errors, items, modifier groups, and modifiers cannot be created
                manually. They can only be added from your POS Menu.
              </Alert>
            )
          )}
          <ResyncSelect
            selectType={MENU_OBJECT_TYPES.categories}
            disabled={posRefreshInProgress}
            hidden={![MENU_OBJECT_TYPES.categories, MENU_OBJECT_TYPES.items].includes(itemType)}
            multipleAllowed={itemType === MENU_OBJECT_TYPES.categories}
            message={message}
          />
          <ResyncSelect
            selectType={MENU_OBJECT_TYPES.items}
            disabled={!selectedOptions[MENU_OBJECT_TYPES.categories]?.length}
            hidden={itemType !== MENU_OBJECT_TYPES.items}
            multipleAllowed={itemType === MENU_OBJECT_TYPES.items}
            message={message}
          />
          <ResyncSelect
            selectType={MENU_OBJECT_TYPES.modifierGroups}
            disabled={posRefreshInProgress}
            hidden={itemType !== MENU_OBJECT_TYPES.modifierGroups}
            multipleAllowed={itemType === MENU_OBJECT_TYPES.modifierGroups}
            message={message}
          />
          <ResyncSelect
            selectType={MENU_OBJECT_TYPES.modifiers}
            disabled={posRefreshInProgress}
            hidden={itemType !== MENU_OBJECT_TYPES.modifiers}
            multipleAllowed={itemType === MENU_OBJECT_TYPES.modifiers}
            message={message}
          />
          {duplicateChildrenToggle}
        </div>
      </SelectItemFromPosDrawer>
      <AddItemDisplayDrawer
        itemType={itemType}
        parentRecord={parentRecord}
        dropdownOptions={dropdownOptions[itemType] || []}
        duplicateChildren={duplicateChildren}
        selectedOptions={selectedOptions[itemType]}
        setSelectedOptions={setSelectedOptions}
        opened={addItemDisplayDrawerOpen}
        openAddItemFromPosDrawer={openAddItemFromPosDrawer}
        closeAddItemDisplayDrawer={addItemDisplayDrawerHandlers.close}
      />
    </>
  );
}

AddItemFromPosDrawer.propTypes = {
  itemType: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  parentRecord: PropTypes.object.isRequired,
  opened: PropTypes.bool.isRequired,
  openAddItemFromPosDrawer: PropTypes.func.isRequired,
  closeAddItemFromPosDrawer: PropTypes.func.isRequired,
  openAddTypeChoiceDrawer: PropTypes.func.isRequired,
};
